import { Flex, Text } from '@lego/klik-ui';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FleetBadge } from '@/components/fleet/FleetBadge';
import { paths } from '@/utility/global_paths';
import { CopyableText } from '../CopyableText';
import { createColumnHelper } from './appTable';
import { TableStoreProvider } from './store/Context';
import { Table } from './Table';

type service = {
  imageName: string;
  tags: { tag: string; digest: string; fleets?: number[] }[];
};

type ServiceTagRow = {
  image: string;
  tag: string;
  digest: string;
  fleets: number[];
};

const columnHelper = createColumnHelper<ServiceTagRow>();

export const ServiceTagsTable = (props: service) => {
  const { workspaceId, repositoryId } = useParams();

  const parsedWorkspaceId = workspaceId ? Number.parseInt(workspaceId, 10) : null;
  const parsedRepositoryId = repositoryId ? Number.parseInt(repositoryId, 10) : null;

  const tableData = useMemo(() => {
    return props.tags
      .toSorted((a, b) => a.tag.localeCompare(b.tag))
      .toReversed()
      .map((entry) => ({
        image: `${props.imageName}:${entry.tag}`,
        tag: entry.tag,
        digest: entry.digest,
        fleets: entry.fleets ?? [],
      }));
  }, [props.tags, props.imageName]);

  const tableColumns = columnHelper.columns([
    columnHelper.accessor('tag', {
      enableSorting: false,
      header: 'Tag',
      id: 'tag',
      cell: (info) => <Text>{info.getValue() as string}</Text>,
    }),
    ...(parsedWorkspaceId != null
      ? [
          columnHelper.accessor('fleets', {
            enableSorting: false,
            header: 'Fleets',
            id: 'fleets',
            size: 200,
            cell: (info) => {
              const fleetIds = info.getValue();
              return fleetIds.length > 0 ? (
                <Flex flexWrap="wrap" gap={1}>
                  {fleetIds.map((id) => (
                    <FleetBadge fleetId={id} key={id} workspaceId={parsedWorkspaceId} />
                  ))}
                </Flex>
              ) : (
                <Text color="slate.500">Unassigned</Text>
              );
            },
          }),
        ]
      : []),
    columnHelper.accessor('digest', {
      enableSorting: false,
      header: 'Digest',
      id: 'digest',
      minSize: 400,
      cell: (info) => (
        <CopyableText
          prefixLength={20}
          suffixLength={0}
          value={info.getValue() as string}
        ></CopyableText>
      ),
    }),
    columnHelper.accessor('image', {
      enableSorting: false,
      header: 'Image',
      id: 'image',
      minSize: 600,
      cell: (info) => (
        <CopyableText
          prefixLength={(info.getValue() as string).length}
          value={info.getValue() as string}
        ></CopyableText>
      ),
    }),
  ]);

  return (
    <TableStoreProvider initialSorting={[{ desc: true, id: 'tag' }]} name="ServiceTags">
      <Table
        columns={tableColumns}
        data={tableData}
        rowLink={
          parsedWorkspaceId != null && parsedRepositoryId != null
            ? ({ tag }) => paths.imageTagById(parsedWorkspaceId, parsedRepositoryId, tag)
            : undefined
        }
        title="Available Tags"
      />
    </TableStoreProvider>
  );
};
